import React, { useState, useEffect } from 'react';
import api from '../config/api';
import PageNavbar from './PageNavbar';
import './Desserts.css';

const Desserts = () => {
  const [desserts, setDesserts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDessert, setEditingDessert] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    default_price: '',
    active: true
  });

  const fetchDesserts = async () => {
    try {
      const response = await api.get('/users/desserts/list');
      setDesserts(response.data.desserts || []);
    } catch (error) {
      console.error('Error fetching desserts:', error);
      setDesserts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDesserts();
  }, []);

  const resetForm = () => {
    setFormData({ name: '', description: '', default_price: '', active: true });
    setEditingDessert(null);
    setShowForm(false);
  };

  const handleEdit = (dessert) => {
    setEditingDessert(dessert);
    setFormData({
      name: dessert.name || '',
      description: dessert.description || '',
      default_price: dessert.default_price != null ? String(dessert.default_price) : '',
      active: dessert.active !== false
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        default_price: formData.default_price.trim() !== '' ? formData.default_price : null,
        active: formData.active
      };
      if (editingDessert) {
        await api.put(`/users/desserts/${editingDessert.id}`, payload);
        alert('Dessert updated successfully');
      } else {
        await api.post('/users/desserts', payload);
        alert('Dessert added. Order creators can select it when adding Desserts to an order.');
      }
      resetForm();
      fetchDesserts();
    } catch (error) {
      alert(error.response?.data?.error || `Error ${editingDessert ? 'updating' : 'adding'} dessert`);
    }
  };

  const handleToggleActive = async (dessert) => {
    if (!window.confirm(`${dessert.active ? 'Deactivate' : 'Activate'} "${dessert.name}"? Inactive items will not appear in the order form.`)) return;
    try {
      await api.put(`/users/desserts/${dessert.id}`, { active: !dessert.active });
      fetchDesserts();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update dessert');
    }
  };

  return (
    <div className="desserts-page">
      <PageNavbar />
      <div className="container">
        <div className="desserts-header">
          <h1>Desserts</h1>
          <button
            className="btn btn-primary"
            onClick={() => {
              if (showForm) resetForm();
              else setShowForm(true);
            }}
          >
            {showForm ? 'Cancel' : 'Add Dessert'}
          </button>
        </div>

        {showForm && (
          <div className="card">
            <h2>{editingDessert ? 'Edit Dessert' : 'Add New Dessert'}</h2>
            <p className="desserts-hint">Order creators will select from this list when adding &quot;Desserts&quot; to an order. Set name and default price (₹).</p>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g. Gulab Jamun"
                />
              </div>
              <div className="form-group">
                <label>Description (optional)</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Short description"
                />
              </div>
              <div className="form-group">
                <label>Default price (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-control"
                  value={formData.default_price}
                  onChange={(e) => setFormData({ ...formData, default_price: e.target.value })}
                  placeholder="e.g. 50"
                />
              </div>
              {editingDessert && (
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    />
                    {' '}Active (show in order form)
                  </label>
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="submit" className="btn btn-primary">
                  {editingDessert ? 'Update Dessert' : 'Add Dessert'}
                </button>
                {editingDessert && (
                  <button type="button" className="btn btn-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="loading">Loading desserts...</div>
        ) : (
          <div className="card">
            <h2>All Desserts</h2>
            <p className="desserts-hint">Active desserts appear in the order form when creating or editing orders. Toggle inactive to hide from the list without deleting.</p>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Default price (₹)</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {desserts.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="no-data">No desserts yet. Click &quot;Add Dessert&quot; to create one. Order creators will then select from this list.</td>
                    </tr>
                  ) : (
                    desserts.map((d) => (
                      <tr key={d.id} className={d.active ? '' : 'dessert-inactive'}>
                        <td><strong>{d.name}</strong></td>
                        <td>{d.description || '-'}</td>
                        <td>{d.default_price != null ? `₹${parseFloat(d.default_price).toFixed(2)}` : '-'}</td>
                        <td>
                          <span className={`status-badge ${d.active ? 'status-active' : 'status-inactive'}`}>
                            {d.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleEdit(d)}
                            style={{ padding: '5px 12px', fontSize: '14px', marginRight: '8px' }}
                          >
                            Edit
                          </button>
                          <button
                            className={`btn btn-sm ${d.active ? 'btn-secondary' : 'btn-success'}`}
                            onClick={() => handleToggleActive(d)}
                            style={{ padding: '5px 12px', fontSize: '14px' }}
                          >
                            {d.active ? 'Deactivate' : 'Activate'}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Desserts;
